import React, { useState, useRef, useEffect } from "react";
import Pagination from "../../components/Pagination";
import { Icon } from "@iconify/react";
import { formatDate } from "../../../utils/DateUtils";

export default function JobFileInvoiceTable({
  invoices = [],
  onEdit,
  onDelete,
  onAddInvoice,
  onPrint,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Debug: ตรวจสอบว่า onAddInvoice ถูกส่งมาหรือไม่
  useEffect(() => {
    console.log('JobFileInvoiceTable - onAddInvoice:', typeof onAddInvoice, onAddInvoice);
  }, [onAddInvoice]);

  const actionRef = useRef(null);

  const [rightOffset, setRightOffset] = useState(0);

  useEffect(() => {
    if (actionRef.current) {
      setRightOffset(actionRef.current.getBoundingClientRect().width);
    }
  }, []);

  const totalItems = invoices.length;
  const from = (currentPage - 1) * itemsPerPage;
  const paginated = invoices.slice(from, from + itemsPerPage);

  // กรอง invoice ที่ไม่ใช่งวดเสริม (ไม่มี "/" ใน Invoice_No)
  const mainInvoices = invoices.filter(inv => {
    const invoiceNo = inv.Invoice_No || "";
    return !invoiceNo.includes("/");
  });

  // คำนวณผลรวมเฉพาะงวดหลัก (ไม่รวมงวดเสริม) สำหรับยอดที่ต้องชำระ
  const totalToPay = mainInvoices.reduce((sum, inv) => sum + (Number(inv.ToPay) || 0), 0);

  // คำนวณผลรวมยอดที่ชำระมา รวมทั้งงวดหลักและงวดเสริม
  const totalPaid = invoices.reduce((sum, inv) => sum + (Number(inv.Paid) || 0), 0);

  // คำนวณยอดที่ค้างชำระ = ยอดที่ต้องชำระ - ยอดที่ชำระมา
  const totalRemaining = Math.max(0, totalToPay - totalPaid);

  // ฟังก์ชันคำนวณวันที่ครบกำหนดชำระ (ใช้ Due_Date ถ้ามี หรือคำนวณจากวันที่ออก Invoice + 30 วัน)
  const calculateDueDate = (invoiceDate, dueDate) => {
    // ถ้ามี Due_Date จาก database ให้ใช้เลย
    if (dueDate) {
      try {
        const date = new Date(dueDate);
        if (!isNaN(date.getTime())) {
          return formatDate(date.toISOString());
        }
      } catch (error) {
        // ถ้า parse ไม่ได้ให้คำนวณจาก invoiceDate แทน
      }
    }
    
    // ถ้าไม่มี Due_Date ให้คำนวณจากวันที่ออก Invoice + 30 วัน
    if (!invoiceDate) return "-";
    
    try {
      // Parse date - รองรับทั้งรูปแบบ DD/MM/YYYY และ DD-MM-YYYY
      let date;
      if (invoiceDate.includes("/")) {
        const [day, month, year] = invoiceDate.split("/");
        // ถ้าเป็นปี พ.ศ. (มากกว่า 2200) ให้แปลงเป็น ค.ศ.
        const christianYear = parseInt(year) > 2200 ? parseInt(year) - 543 : parseInt(year);
        date = new Date(christianYear, parseInt(month) - 1, parseInt(day));
      } else if (invoiceDate.includes("-")) {
        const [day, month, year] = invoiceDate.split("-");
        const christianYear = parseInt(year) > 2200 ? parseInt(year) - 543 : parseInt(year);
        date = new Date(christianYear, parseInt(month) - 1, parseInt(day));
      } else {
        date = new Date(invoiceDate);
      }
      
      if (isNaN(date.getTime())) return "-";
      
      // เพิ่ม 30 วัน
      date.setDate(date.getDate() + 30);
      
      // Format เป็นรูปแบบไทย (DD/MM/YYYY พ.ศ.)
      return formatDate(date.toISOString());
    } catch (error) {
      return "-";
    }
  };

  return (
    <div
      className="rounded-0 border-0"
      style={{
        display: "flex",
        flexDirection: "column",
        maxHeight: "calc(100vh - 250px)",
        overflow: "hidden",
      }}
    >
      <style>{`
        table {
            border-collapse: separate !important;
            border-spacing: 1px !important;
            width: 100%;
            min-width: 1500px;
        }

        th, td {
            padding: 8px 10px !important;
            background: white !important;
            text-align: center;
            outline: 1px solid #d0d0d0;
            outline-offset: -1px;
            white-space: nowrap;
        }

        thead th {
            background-color: #E13F57 !important;
            color: white !important;
            font-weight: 600;
        }

        .sticky-action {
            position: sticky;
            right: 0;
            z-index: 20;
            color: white !important;
        }

        thead th.sticky-action {
            background-color: #E13F57 !important;
            z-index: 21 !important;
        }

        th.sticky-action,
        td.sticky-action {
            min-width: 168px !important;
            width: 168px !important;
            vertical-align: middle !important;
        }

        .invoice-actions-grid {
            display: grid !important;
            grid-template-columns: repeat(4, 36px);
            column-gap: 6px;
            justify-content: center;
            align-items: center;
            margin: 0 auto;
        }

        .invoice-actions-grid > div {
            width: 36px;
            height: 34px;
            display: flex !important;
            align-items: center;
            justify-content: center;
        }

        tbody tr.bg-gray-50 td {
            background-color: #f9fafb !important;
            font-weight: 600;
        }
      `}</style>

      {/* TABLE */}
      <div className="flex-1 table-wrapper-scroll" style={{ overflowX: "auto", overflowY: "auto" }}>
        <table className="table mb-0">
          <thead>
            <tr>
              <th>ลำดับ</th>
              <th>เลขที่ใบแจ้งหนี้</th>
              <th>วันที่ออก Inv</th>
              <th>กำหนดชำระภายในวันที่</th>
              <th>ยอดที่ต้องชำระ</th>
              <th>ยอดที่ชำระมา</th>
              <th>ยอดที่ค้างชำระ</th>
              <th>วันที่ชำระ</th>
              <th>หมายเหตุ</th>
              <th>สถานะ</th>
              <th>สถานะเกิน Due</th>

              <th ref={actionRef} className="sticky-action">
                การจัดการ
              </th>
            </tr>
          </thead>

          <tbody>
            {paginated.length ? (
              <>
                {paginated.map((row, i) => (
                  <tr key={row.Invoice_No + i}>
                    <td>{from + i + 1}</td>
                    <td>{row.Invoice_No}</td>
                    <td>{row.Date}</td>
                    <td>{calculateDueDate(row.Date, row.Due_Date)}</td>
                    <td>{Number(row.ToPay || 0).toLocaleString()}</td>
                    <td>{Number(row.Paid || 0).toLocaleString()}</td>
                    <td>
                      {(() => {
                        const toPay = Number(row.ToPay || 0);
                        const paid = Number(row.Paid || 0);
                        const remaining = Math.max(0, toPay - paid);
                        return remaining > 0 ? remaining.toLocaleString() : "-";
                      })()}
                    </td>
                    <td>{row.PayDate || "-"}</td>
                    <td className="text-start">{row.Remark || "-"}</td>

                    <td>
                      {(() => {
                        const paid = Number(row.Paid || 0);
                        const toPay = Number(row.ToPay || 0);
                        // ตรวจสอบว่าชำระครบหรือไม่
                        const isFullyPaid = paid >= toPay && toPay > 0;
                        // ตรวจสอบว่ามีการชำระแล้วหรือไม่ (แม้จะไม่ครบ)
                        const hasPaid = paid > 0;
                        // ตรวจสอบว่าค้างชำระหรือไม่ (เกินกำหนดและยังไม่ชำระครบ)
                        const isOverdue = row.Status === "overdue" && !isFullyPaid;
                        
                        if (isFullyPaid) {
                          return <span className="badge bg-success">ชำระครบ</span>;
                        } else if (hasPaid) {
                          return <span className="badge bg-success">ชำระแล้ว</span>;
                        } else if (isOverdue) {
                          return <span className="badge bg-danger">ค้างชำระ</span>;
                        } else {
                          return <span className="badge bg-warning text-dark">รอชำระ</span>;
                        }
                      })()}
                    </td>

                    <td>
                      {row.Status === "overdue" ? "เกินกำหนด" : "-"}
                    </td>

                    <td className="sticky-action">
                      <div className="invoice-actions-grid">
                        {/* ช่อง 1: เพิ่มงวดเสริม — เว้นช่องว่างถ้าไม่แสดงปุ่ม เพื่อไม่ให้ icon คอลัมน์ถัดไปเลื่อน */}
                        <div>
                          {(() => {
                            const paid = Number(row.Paid || 0);
                            const toPay = Number(row.ToPay || 0);
                            let shouldShow = paid > 0 && paid < toPay && toPay > 0;

                            if (shouldShow && row.Invoice_No && row.Invoice_No !== "-") {
                              let baseInvoiceNo = row.Invoice_No;
                              if (row.Invoice_No.includes("/")) {
                                baseInvoiceNo = row.Invoice_No.split("/")[0];
                              }

                              const subInvoices = invoices.filter(inv => {
                                const invoiceNo = inv.Invoice_No || "";
                                return invoiceNo.includes("/") &&
                                       invoiceNo.startsWith(baseInvoiceNo + "/") &&
                                       invoiceNo !== row.Invoice_No;
                              });

                              if (subInvoices.length > 0) {
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);

                                let currentSubNumber = 0;
                                if (row.Invoice_No.includes("/")) {
                                  const parts = row.Invoice_No.split("/");
                                  if (parts.length === 2) {
                                    const subNum = parseInt(parts[1], 10);
                                    if (!isNaN(subNum)) {
                                      currentSubNumber = subNum;
                                    }
                                  }
                                }

                                const hasActiveSubInvoice = subInvoices.some(inv => {
                                  if (inv.Invoice_No.includes("/")) {
                                    const parts = inv.Invoice_No.split("/");
                                    if (parts.length === 2 && parts[0] === baseInvoiceNo) {
                                      const subNum = parseInt(parts[1], 10);
                                      if (!isNaN(subNum) && subNum > currentSubNumber) {
                                        if (!inv.Due_Date) return true;

                                        try {
                                          const dueDate = new Date(inv.Due_Date);
                                          dueDate.setHours(0, 0, 0, 0);
                                          return today <= dueDate;
                                        } catch (error) {
                                          console.error("Error parsing Due_Date:", error, inv.Due_Date);
                                          return true;
                                        }
                                      }
                                    }
                                  }
                                  return false;
                                });

                                if (hasActiveSubInvoice) {
                                  shouldShow = false;
                                }
                              }
                            }

                            if (!shouldShow) return null;

                            const remainingAmount = toPay - paid;
                            const btnShell = {
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                              width: "32px",
                              height: "32px",
                              padding: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            };
                            return (
                              <button
                                type="button"
                                className="btn btn-sm btn-success text-white"
                                style={{ ...btnShell, backgroundColor: "#10b981" }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (onAddInvoice) {
                                    onAddInvoice({
                                      ...row,
                                      remainingAmount: remainingAmount,
                                      allInvoices: invoices
                                    });
                                  }
                                }}
                                title={`สร้าง Invoice ใหม่ (ยอดที่เหลือ: ${remainingAmount.toLocaleString()})`}
                              >
                                <Icon icon="mdi:plus" width={18} />
                              </button>
                            );
                          })()}
                        </div>

                        <div>
                          <button
                            type="button"
                            className="btn btn-sm btn-warning text-white"
                            style={{
                              backgroundColor: "#f59e0b",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                              width: "32px",
                              height: "32px",
                              padding: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                            onClick={() => onEdit?.(row)}
                            title="แก้ไข"
                          >
                            <Icon icon="mdi:pencil" width={18} />
                          </button>
                        </div>

                        <div>
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={{
                              backgroundColor: "transparent",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                              width: "32px",
                              height: "32px",
                              padding: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                            onClick={() => onPrint?.(row)}
                            title="พิมพ์"
                          >
                            <Icon icon="mdi:printer" width={18} color="#2563eb" />
                          </button>
                        </div>

                        <div>
                          {(() => {
                            const invoiceNo = row.Invoice_No || "";
                            const isSubInvoice = invoiceNo.includes("/");
                            if (!isSubInvoice || !onDelete) return null;
                            return (
                              <button
                                type="button"
                                className="btn btn-sm btn-danger text-white"
                                style={{
                                  backgroundColor: "#ef4444",
                                  border: "none",
                                  borderRadius: "4px",
                                  cursor: "pointer",
                                  width: "32px",
                                  height: "32px",
                                  padding: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                                onClick={() => onDelete?.(row)}
                                title="ลบงวดเสริม"
                              >
                                <Icon icon="mdi:delete" width={18} />
                              </button>
                            );
                          })()}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
                {/* Summary Row */}
                <tr className="bg-gray-50 font-semibold">
                  <td colSpan={4} className="text-end">รวม:</td>
                  <td className="text-gray-900">{totalToPay.toLocaleString()}</td>
                  <td className="text-gray-900">{totalPaid.toLocaleString()}</td>
                  <td className="text-gray-900">{totalRemaining.toLocaleString()}</td>
                  <td colSpan={4}></td>
                  <td className="sticky-action"></td>
                </tr>
              </>
            ) : (
              <tr>
                <td colSpan={12} className="text-center py-4 text-gray-500">
                  ไม่มีข้อมูลใบแจ้งหนี้
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      <Pagination
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        onItemsPerPageChange={(val) => {
          setItemsPerPage(val);
          setCurrentPage(1);
        }}
        onPageChange={setCurrentPage}
      />
    </div>
  );
}
